import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { SystemZone } from 'luxon';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { IUser, NO_AUTH, NO_CACHE, SessionTokens } from '@app/models';

const DEL = 'DELETE';
const GET = 'GET';
const PAT = 'PATCH';
const POS = 'POST';
const PUT = 'PUT';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  get tokens() {
    return this._tokens;
  }
  private _tokens: { access: string; refresh: string };
  private api = `${environment.apiUrl}/v1`;
  private appLocale = inject(LOCALE_ID);
  private cfg: object = {
    context: new HttpContext().set(NO_AUTH, false).set(NO_CACHE, false),
    observe: 'body' as const,
    responseType: 'json' as const,
    withCredentials: false,
  };
  private http = inject(HttpClient);
  private refreshing = false;
  private refreshing$ = new Subject<boolean>();
  private router = inject(Router);

  constructor() {
    this._tokens = { access: '', refresh: '' };
    const tokens = localStorage.getItem('tokens');
    if (tokens) this._tokens = JSON.parse(tokens);
  }

  clearTokens() {
    this.storeTokens({ access_token: '', refresh_token: '' });
  }

  confirmStripePayment(paymentId: string) {
    return this.go(POS, `${this.api}/payments/stripe/confirm`, {
      ...this.cfg,
      body: { paymentId },
    });
  }

  getCourse(courseId: string) {
    return this.go(GET, `${this.api}/courses/${courseId}`, this.cfg);
  }

  getEnrollPrice() {
    interface PriceRecord {
      amount: number;
      codename: string;
      effectiveDate: string;
    }
    return this.go<PriceRecord>(GET, `${this.api}/prices/enroll`, this.cfg);
  }

  getMediaFileUrl(path: string) {
    // TODO Move to .env
    // return of(`https://fluentstride-dev.s3.amazonaws.com/${path}`);
    return this.go<string>(GET, `${this.api}/media/${path}`, {
      ...this.cfg,
      responseType: 'text',
    });
  }

  getMyCohort() {
    return this.go<any>(GET, `${this.api}/my/cohort`, {
      ...this.cfg,
      params: { timezone: SystemZone.instance.name },
    });
  }

  getMyCourses() {
    return this.go<any[]>(GET, `${this.api}/my/courses`, this.cfg);
  }

  getMyProfile() {
    return this.go<IUser>(GET, `${this.api}/my/profile`, {
      ...this.cfg,
      params: { inject: ['cohort', 'courses'] },
    });
  }

  getTask(taskId: string) {
    return this.go<any>(GET, `${this.api}/tasks/${taskId}`, this.cfg);
  }

  initStripePayment(idempotencyKey?: string) {
    return this.go<{ clientSecret: string }>(
      POS,
      `${this.api}/payments/stripe/init`,
      {
        ...this.cfg,
        body: { idempotencyKey: idempotencyKey ?? null },
      },
    );
  }

  loadCohorts(
    showUnavailable = false,
    courseId?: string,
    inject?: string | string[],
    timezone?: string,
  ) {
    return this.go(GET, `${this.api}/cohorts`, {
      ...this.cfg,
      params: {
        showUnavailable,
        courseId: courseId || '',
        inject: inject || '',
        timezone: timezone || '',
      },
    });
  }

  loadCourses() {
    return this.go(GET, `${this.api}/courses`, this.cfg);
  }

  loadLanguages() {
    return this.go(GET, `${this.api}/languages`, {
      ...this.cfg,
      params: { showUnavailable: true },
    });
  }

  requestLocale(locale: string) {
    return this.go(GET, `${this.api}/request/${locale}`, this.cfg);
  }

  sendMyVerificationEmail() {
    return this.go(GET, `${this.api}/emailer/send/verify`, {
      ...this.cfg,
      params: { APP_LOCALE: this.appLocale },
    });
  }

  sendMyWelcomeEmail() {
    return this.go(GET, `${this.api}/emailer/send/welcome`, {
      ...this.cfg,
      params: { APP_LOCALE: this.appLocale },
    });
  }

  setMyCohort(cohortId: string) {
    return this.go(PAT, `${this.api}/my/cohort`, {
      ...this.cfg,
      body: { id: cohortId },
    });
  }

  setMyCourses() {
    return this.go(POS, `${this.api}/my/courses`, this.cfg);
  }

  setMyIdentifiers(identifiers: any) {
    const { email, firstName, lastName, nativeLocale } = identifiers;
    return this.go(PAT, `${this.api}/my/profile`, {
      ...this.cfg,
      body: { email, firstName, lastName, nativeLocale },
    });
  }

  setMyPassword(password: string) {
    const context = new HttpContext().set(NO_CACHE, true);
    return this.go(PAT, `${this.api}/my/password`, {
      ...this.cfg,
      body: { password: password || '' },
      context,
    });
  }

  setUserLevel(userId: string, courseLevel: number) {
    return this.go<any[]>(
      POS,
      `${this.api}/users/${userId}/progress/courses/level`,
      {
        ...this.cfg,
        body: { level: +courseLevel },
      },
    );
  }

  signIn(email: string, password: string) {
    const context = new HttpContext().set(NO_AUTH, true).set(NO_CACHE, true);
    return this.http.request<SessionTokens>(POS, `${this.api}/auth/signin`, {
      ...this.cfg,
      body: { email, password },
      context,
      params: { APP_LOCALE: this.appLocale },
    });
  }

  signOut() {
    return this.go<void>(POS, `${this.api}/auth/signout`, {
      ...this.cfg,
      body: undefined,
    });
  }

  signUp(email: string, password: string, firstName: string, lastName: string) {
    const context = new HttpContext().set(NO_AUTH, true).set(NO_CACHE, true);
    return this.http.request<SessionTokens>(POS, `${this.api}/auth/signup`, {
      ...this.cfg,
      body: { email, password, firstName, lastName },
      context,
      params: { APP_LOCALE: this.appLocale },
    });
  }

  storeTokens(tokens: SessionTokens) {
    this._tokens.access = tokens.access_token;
    this._tokens.refresh = tokens.refresh_token;
    localStorage.setItem('tokens', JSON.stringify(this._tokens));
  }

  private go<R>(method: string, url: string, config: object) {
    return this.http
      .request<R>(method, url, config)
      .pipe(catchError(this.refresh.bind(this))) as Observable<R>;
  }
  private refresh<R>(err: any, obs: Observable<R>) {
    // Ignore everything except signed out.
    if (err.status !== 401) {
      throw err;
    }
    // Debounce the refresh, but return the
    // *original* (caught) observable.
    if (this.refreshing) {
      return this.refreshing$.pipe(
        switchMap((v) => {
          if (v) return obs;
          throw err;
        }),
        take(1),
      );
    }
    this.refreshing = true;
    // Use the refresh token to get a new access token,
    // then return the *original* (caught) observable.
    const refreshToken = this.tokens.refresh;
    return this.http
      .post<SessionTokens>(`${this.api}/auth/refresh`, { refreshToken })
      .pipe(
        catchError((e) => {
          this.refreshing = false;
          this.refreshing$.next(false);
          this.resolveAccessErr(e);
          throw err;
        }),
        switchMap((v) => {
          this.storeTokens(v);
          this.refreshing = false;
          this.refreshing$.next(true);
          return obs;
        }),
      );
  }
  private resolveAccessErr(err: any) {
    switch (err.status) {
      case 401:
        void this.router.navigate(['/signin'], {
          queryParams: { redirect: this.router.url },
        });
        break;
      // case 403:
      //   void this.router.navigate(['/unauthorized'], { queryParams: { redirect: this.router.url }});
      //   break;
    }
    return throwError(() => err);
  }
}
